import type {
  AirQualityPoint,
  DamReading,
  DroughtBrief,
  IncidentFeature,
  WeatherSnapshot,
} from "@sikhio/shared";
import { DAM_COLOR, DAM_LABEL_TH, DROUGHT_COLOR, DROUGHT_LABEL_TH } from "../lib/waterSecurity";
import { Arrow } from "./Arrow";

/**
 * Mayor's Brief — focused mobile-first view for the Mayor of Sikhio.
 *
 * One screen, one decision. Renders only the four numbers that matter
 * during a meeting or site visit:
 *
 *   1.  Lam Takhong storage  — the headline water-security number
 *   2.  Top three incidents  — what is happening on the ground right now
 *   3.  Weather + Air        — is it safe to be outside, is the air clean
 *   4.  Drought band         — how dry are we vs the climatology
 *
 * Everything else (map, 30+ feeds, layer catalogue, CCTV wall) is one tap
 * away through "ดูแผนที่เต็ม" (Open full map). The brief is deliberately
 * scannable, not exploratory — the mayor reads it in 15 seconds at a
 * cabinet meeting, then decides whether to open the full dashboard.
 *
 * Visual lineage: Dieter Rams / Braun. Warm paper, hairline dividers,
 * no border-radius, no shadow, weight ceiling 600, two signal channels
 * (--blue identity, --red live). Trilingual default — TH primary,
 * EN secondary in mono, ZH registered for the brand block.
 */
interface Props {
  dam: DamReading | null;
  damTier: string | null;
  damAge: number;
  weather: WeatherSnapshot | null;
  air: AirQualityPoint | null;
  drought: DroughtBrief | null;
  /** Top three active incidents, ordered by recency × severity. */
  incidents: IncidentFeature[];
  onOpenFullMap: () => void;
  /** Opens the concierge. The brief is the phone's DEFAULT surface, and
   *  the chat was unreachable there: brief mode hid `.chatbox` outright,
   *  so the one device a mayor is most likely to use had no way to ask
   *  a question. The brief carries its own trigger, beside its own. */
  onOpenChat: () => void;
  /** Opens the Manual sheet. Only surfaced where the top bar hides its own
   *  trigger, which is why the button is width-gated rather than always on. */
  onOpenManual: () => void;
  /** Last successful refresh across any feed, for the footer. */
  updatedAt: Date | null;
}

// Ranked most-severe first. Keyed loosely on purpose: adapters normalise
// severity to the four IncidentSeverity values, but a stray upstream value
// must sort last rather than crash the Mayor's brief.
const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

function fmtRelative(iso: string | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diffMin = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (diffMin < 60) return `${diffMin} นาทีที่แล้ว`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `${h} ชั่วโมงที่แล้ว`;
  const d = Math.round(h / 24);
  return `${d} วันที่แล้ว`;
}

function pm25Category(pm25: number | null): { label: string; tone: string } {
  if (pm25 == null) return { label: "—", tone: "var(--text-3)" };
  if (pm25 <= 25) return { label: "อากาศดี", tone: "var(--ink)" };
  if (pm25 <= 50) return { label: "ปานกลาง", tone: "var(--ink-2)" };
  if (pm25 <= 100) return { label: "เริ่มมีผลกระทบ", tone: "var(--ink-2)" };
  return { label: "อันตราย", tone: "var(--red)" };
}

function fmtTemp(t: number | null): string {
  if (t == null) return "—";
  return `${Math.round(t)}°`;
}

function fmtPct(p: number | null | undefined): string {
  if (p == null) return "—";
  return `${p.toFixed(1)}%`;
}

export function MayorsBrief({
  dam,
  damTier,
  damAge,
  weather,
  air,
  drought,
  incidents,
  onOpenFullMap,
  onOpenChat,
  onOpenManual,
  updatedAt,
}: Props) {
  // Sort incidents: critical > high > medium > low, then newest first.
  const top = [...incidents]
    .sort((a, b) => {
      const sev = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0);
      if (sev !== 0) return sev;
      return new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime();
    })
    .slice(0, 3);

  const damTone = dam ? DAM_COLOR[dam.alertLevel] ?? "var(--ink)" : "var(--text-3)";
  const damLabel = dam ? DAM_LABEL_TH[dam.alertLevel] : "รอข้อมูล";
  const droughtTone = drought ? DROUGHT_COLOR[drought.band] : "var(--text-3)";
  const droughtLabel = drought ? DROUGHT_LABEL_TH[drought.band] : "รอข้อมูล";

  const airPm25 = air?.pm25 ?? null;
  const airInfo = pm25Category(airPm25);

  return (
    <main className="mayor-brief" lang="th">
      <header className="mayor-brief-head">
        <div className="mayor-brief-eyebrow">
          <span className="brief-tag mono">สรุปสถานการณ์ · MAYOR BRIEF</span>
          {/* In brief mode the mobile nav and rails are hidden, so this is the
              only always-reachable way back to the working dashboard. The
              footer CTA exists too, but it is below four screens of content. */}
          <button
            type="button"
            className="mayor-brief-exit mono"
            onClick={onOpenFullMap}
            aria-label="กลับไปแดชบอร์ดเต็มพร้อมแผนที่และเลเยอร์ทั้งหมด"
          >
            <Arrow direction="left" /> กลับสู่แดชบอร์ด
          </button>
          {/* The concierge, on the brief's own surface. Measured at
              390x844: brief mode set `.chatbox { display: none }`, and the
              only other trigger lived in the bottom bar, which phones
              hide — so the phone's default screen had NO way to ask a
              question. One trigger per surface: the floating handle on the
              map, this one in the brief. */}
          <button
            type="button"
            className="mayor-brief-ask mono"
            onClick={onOpenChat}
            aria-haspopup="dialog"
            aria-label="ถามผู้ช่วยเกี่ยวกับข้อมูลในบทสรุปนี้"
          >
            ถาม STM
          </button>
        </div>
        <h1 className="mayor-brief-title">เทศบาลเมืองสีคิ้ว</h1>
        <div className="mayor-brief-meta mono">
          {updatedAt ? (
            <>
              อัปเดต {updatedAt.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })} น.
              {damAge > 0 && <> · เขื่อน {damAge} นาทีที่แล้ว</>}
            </>
          ) : (
            "กำลังโหลดข้อมูล…"
          )}
        </div>
      </header>

      {/* ── Hero: Lam Takhong ─────────────────────────────────────────── */}
      <section className="mayor-brief-hero" aria-label="สถานะเขื่อนลำตะคอง">
        <div className="mayor-brief-hero-label">
          <span className="brief-tag">เขื่อนลำตะคอง · LAM TAKHONG</span>
          <span className="mayor-brief-hero-band mono" style={{ color: damTone }}>
            {damLabel}
            {damTier && damTier !== "live" && (
              <span className="mayor-brief-tier-suffix"> · {damTier}</span>
            )}
          </span>
        </div>
        <div className="mayor-brief-hero-figure">
          <span className="mayor-brief-hero-number mono">
            {fmtPct(dam?.pctNormal)}
          </span>
          <span className="mayor-brief-hero-unit">ของ รนก.</span>
        </div>
        <div className="mayor-brief-hero-bar" role="img" aria-label={`ระดับน้ำ ${fmtPct(dam?.pctNormal)} ของความจุปกติ`}>
          <div
            className="mayor-brief-hero-bar-fill"
            style={{
              width: `${Math.max(0, Math.min(100, dam?.pctNormal ?? 0))}%`,
              background: damTone,
            }}
          />
        </div>
        <dl className="mayor-brief-hero-detail mono">
          <div>
            <dt>เก็บกัก</dt>
            <dd>{dam ? `${dam.storageMcm.toFixed(2)} ล้าน ลบ.ม.` : "—"}</dd>
          </div>
          <div>
            <dt>ความจุปกติ</dt>
            <dd>{dam ? `${dam.normalStorageMcm.toFixed(2)} ล้าน ลบ.ม.` : "—"}</dd>
          </div>
          <div>
            <dt>น้ำเข้า/วัน</dt>
            <dd>{dam?.inflowMcmDay != null ? `${dam.inflowMcmDay.toFixed(2)} ลบ.ม.` : "—"}</dd>
          </div>
        </dl>
      </section>

      {/* ── Top incidents ─────────────────────────────────────────────── */}
      <section className="mayor-brief-section" aria-label="แจ้งเตือนล่าสุด">
        <div className="mayor-brief-section-head">
          <span className="brief-tag">แจ้งเตือนล่าสุด · INCIDENTS</span>
          {/* MSH: with no incidents this badge read "ไม่มี" and the empty line
              right below it read "ไม่มีรายงานในขณะนี้" — the same sentence
              twice, 30px apart, in two different weights. The empty state
              already says it, so the badge only appears when there is a
              count worth reading. */}
          {top.length > 0 && (
            <span className="mayor-brief-section-count mono">
              {top.length} จาก {incidents.length}
            </span>
          )}
        </div>
        {top.length === 0 ? (
          <div className="mayor-brief-empty mono">ไม่มีรายงานในขณะนี้</div>
        ) : (
          <ul className="mayor-brief-list">
            {top.map((it) => (
              <li key={it.id} className={`mayor-brief-item sev-${it.severity}`}>
                <div className="mayor-brief-item-row">
                  <span className="mayor-brief-item-sev mono">{it.severity}</span>
                  <span className="mayor-brief-item-title">{it.title}</span>
                </div>
                <div className="mayor-brief-item-meta mono">
                  <span>{it.category}</span>
                  <span aria-hidden="true">·</span>
                  <span>{fmtRelative(it.reportedAt)}</span>
                  {it.sourceUrl && (
                    <>
                      <span aria-hidden="true">·</span>
                      <a href={it.sourceUrl} target="_blank" rel="noopener noreferrer">
                        ที่มา ↗
                      </a>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Weather / Air / Drought ─────────────────────────────────────
          MSH: three equal `1fr` cards, each an unlabelled figure — `11` and
          `0` gave the reader no name for what they measured, and repeating
          three identical blocks flattens the journey instead of ranking it.
          Weather is the one an operator feels, so it leads at full width and
          full figure size; air and drought sit beneath it as a labelled pair
          at a smaller step. Content determines the size, the task determines
          the composition. */}
      <section className="mayor-brief-grid" aria-label="สภาพอากาศ อากาศ ภัยแล้ง">
        <article className="mayor-brief-card mayor-brief-card--lead" aria-label="สภาพอากาศ">
          <div className="mayor-brief-card-label mono">สภาพอากาศ · WEATHER</div>
          <div className="mayor-brief-card-figure mono">{fmtTemp(weather?.tempC ?? null)}</div>
          <div className="mayor-brief-card-sub mono">
            {weather?.condition ?? "—"}
            {weather?.humidity != null && <> · ความชื้น {Math.round(weather.humidity)}%</>}
          </div>
        </article>

        <div className="mayor-brief-grid-pair">
          <article className="mayor-brief-card mayor-brief-card--minor" aria-label="คุณภาพอากาศ PM2.5">
            <div className="mayor-brief-card-label mono">ฝุ่น PM2.5 · AIR</div>
            <div className="mayor-brief-card-figure mono" style={{ color: airInfo.tone }}>
              {airPm25 != null ? airPm25.toFixed(0) : "—"}
            </div>
            <div className="mayor-brief-card-sub mono">{airInfo.label}</div>
          </article>

          <article className="mayor-brief-card mayor-brief-card--minor" aria-label="ดัชนีภัยแล้ง">
            <div className="mayor-brief-card-label mono">ภัยแล้ง · DROUGHT</div>
            <div className="mayor-brief-card-figure mono" style={{ color: droughtTone }}>
              {drought ? drought.score.toFixed(0) : "—"}
            </div>
            <div className="mayor-brief-card-sub mono">{droughtLabel}</div>
          </article>
        </div>
      </section>

      {/* ── Footer CTA ────────────────────────────────────────────────── */}
      <footer className="mayor-brief-foot">
        <button
          type="button"
          className="mayor-brief-cta"
          onClick={onOpenFullMap}
          aria-label="เปิดแผนที่เต็ม — dashboard แบบเต็มพร้อมแผนที่และเลเยอร์ทั้งหมด"
        >
          <span className="mayor-brief-cta-label">ดูแผนที่เต็ม</span>
          <span className="mayor-brief-cta-sub mono">OPEN FULL MAP · 30+ LIVE FEEDS</span>
          <span aria-hidden="true" className="mayor-brief-cta-arrow"><Arrow direction="up-right" /></span>
        </button>
        {/* The Manual is reachable from the phone's first screen. At ≤720px
            the top bar cannot carry it — the brand, SOURCES, 2D and the theme
            toggle already fill 368 of 390px, and the trigger is hidden
            there — so before this pass the only reference surface a phone
            visitor could open was the source catalog. This button is shown
            ONLY where the top bar's own Manual is hidden, so the two never
            appear together (see .mayor-brief-manual in layout.css). It sits
            beside the "open the full map" action because that is the pair a
            first-time visitor actually needs: go further, or look it up. */}
        <button
          type="button"
          className="mayor-brief-manual mono"
          onClick={onOpenManual}
          aria-haspopup="dialog"
          aria-label="Open user manual — every button, colour, acronym"
        >
          <span aria-hidden="true" className="mayor-brief-manual-icon">?</span>
          <span>คู่มือใช้งาน</span>
        </button>
        <p className="mayor-brief-foot-note mono">
          {/* MSH: this note said two things that were not true here. It named
              Dieter Rams, which is how the thing was designed — process, not
              product, and the Wada provenance block below already carries a
              real attribution with a link. And it told the reader to open a
              browser wider than 900px to reach the full view, when the
              CTA directly above it does exactly that on a phone (verified:
              the shell switches to the full map panel). It was also the
              widest element on the sheet and sat entirely below the fold at
              390x844 — 42 of its 72 pixels under the fold edge. What
              remains is the one true sentence: which mode you are in. */}
          โหมดสรุปสำหรับผู้บริหาร
        </p>
        <p className="wada-provenance">
          <span>Colour palette · Wada Plate 075</span>{" "}
          <span aria-hidden="true">·</span>{" "}
          <a
            href="https://github.com/Nonarkara/palette"
            target="_blank"
            rel="noopener noreferrer"
            title="Sanzo Wada · A Dictionary of Colour Combinations (1933) — sourced via github.com/Nonarkara/palette"
          >
            Sanzo Wada 1933
          </a>
        </p>
      </footer>
    </main>
  );
}
